import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["btn", "clearBtn"]

  connect() {
    this._populating = false
    this.form = this.element.querySelector(".mf-form")
    if (this.form) {
      this._boundOnFormChange = this._onFormChange.bind(this)
      this.form.addEventListener("change", this._boundOnFormChange)
      this._boundOnInput = this._onFormChange.bind(this)
      this.form.addEventListener("input", this._boundOnInput)
      this._boundOnSubmit = this._onSubmit.bind(this)
      this.form.addEventListener("submit", this._boundOnSubmit)
    }

    // On page load, check if current URL params match any template
    this._highlightMatchingTemplate()
  }

  disconnect() {
    if (this.form) {
      if (this._boundOnFormChange) this.form.removeEventListener("change", this._boundOnFormChange)
      if (this._boundOnInput) this.form.removeEventListener("input", this._boundOnInput)
      if (this._boundOnSubmit) this.form.removeEventListener("submit", this._boundOnSubmit)
    }
  }

  select(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const params = JSON.parse(btn.dataset.quickFinderParams)

    this.btnTargets.forEach(b => b.classList.remove("mf-template-btn-active"))
    btn.classList.add("mf-template-btn-active")

    if (this.hasClearBtnTarget) {
      this.clearBtnTarget.style.display = ""
    }

    this._populateForm(params)
  }

  _onFormChange() {
    if (this._populating) return
    // User manually changed a criteria — deselect the template and hide Clear
    this.btnTargets.forEach(b => b.classList.remove("mf-template-btn-active"))
    if (this.hasClearBtnTarget) {
      this.clearBtnTarget.style.display = "none"
    }
  }

  _onSubmit(event) {
    if (this._populating) {
      event.preventDefault()
      return
    }
  }

  _highlightMatchingTemplate() {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.size === 0) return

    const currentParams = this._extractUrlParams(urlParams)

    for (const btn of this.btnTargets) {
      const templateParams = JSON.parse(btn.dataset.quickFinderParams)
      if (this._paramsMatch(currentParams, templateParams)) {
        btn.classList.add("mf-template-btn-active")
        if (this.hasClearBtnTarget) {
          this.clearBtnTarget.style.display = ""
        }
        return
      }
    }
  }

  _extractUrlParams(urlParams) {
    const result = {}
    for (const [key, value] of urlParams.entries()) {
      const cleanKey = key.replace("[]", "")
      if (result[cleanKey]) {
        if (!Array.isArray(result[cleanKey])) {
          result[cleanKey] = [result[cleanKey]]
        }
        result[cleanKey].push(value)
      } else {
        result[cleanKey] = value
      }
    }
    return result
  }

  _paramsMatch(urlParams, templateParams) {
    // Check every template param exists in URL params with matching value
    for (const [key, templateVal] of Object.entries(templateParams)) {
      const urlVal = urlParams[key]
      if (urlVal === undefined) return false

      if (Array.isArray(templateVal)) {
        const urlArr = Array.isArray(urlVal) ? urlVal : [urlVal]
        if (templateVal.length !== urlArr.length) return false
        const sortedTemplate = [...templateVal].sort()
        const sortedUrl = [...urlArr].sort()
        if (!sortedTemplate.every((v, i) => String(v) === String(sortedUrl[i]))) return false
      } else {
        if (String(templateVal) !== String(urlVal)) return false
      }
    }
    return true
  }

  _populateForm(params) {
    this._populating = true

    // Simple selects and inputs
    this._setField("mf_geo_type", params.geo_type)
    this._setField("mf_market_type", params.market_type)
    this._setField("mf_period", params.period)
    this._setField("mf_limit", params.limit)

    // Population filter
    this._setField("mf_pop_filter", params.pop_filter || "any")
    this._setField("mf_pop_min", params.pop_min || "")
    this._setField("mf_pop_max", params.pop_max || "")

    // Price filter
    this._setField("mf_price_filter", params.price_filter || "any")
    this._setField("mf_price_min", params.price_min || "")
    this._setField("mf_price_max", params.price_max || "")

    // Distress trend
    this._setField("mf_distress_trend", params.distress_trend || "any")

    // Hidden fields for rank by
    this._setField("mf_metric_hidden", params.metric || "price")
    this._setField("mf_rank_by_hidden", params.rank_by || "pct")
    this._setField("mf_direction_hidden", params.direction || "rising")
    this._setField("mf_filter_rank_by_hidden", params.filter_rank_by || "none")
    this._setField("mf_filter_rank_dir_hidden", params.filter_rank_dir || "high_to_low")

    // Derive rank_select value from params
    let rankSelectVal
    if (params.filter_rank_by && params.filter_rank_by !== "none") {
      rankSelectVal = "market_condition"
    } else if (params.metric === "inventory") {
      rankSelectVal = "inventory_movement"
    } else {
      rankSelectVal = "price_movement"
    }
    this._setField("mf_rank_select", rankSelectVal)

    // Direction select
    this._setField("mf_direction", params.direction || "rising")

    // Inventory movement select
    if (params.metric === "inventory") {
      this._setField("mf_inventory_movement", params.rank_by || "pct")
    }

    // Market condition selects
    if (params.filter_rank_by && params.filter_rank_by !== "none") {
      this._setField("mf_filter_rank_by", params.filter_rank_by)
      this._setField("mf_filter_rank_dir", params.filter_rank_dir || "high_to_low")
    }

    // Multiselect checkboxes
    this._setCheckboxes("distress_filter[]", params.distress_filter || [])
    this._setCheckboxes("investor_filter[]", params.investor_filter || [])
    this._setCheckboxes("cashflow_tier[]", params.cashflow_tier || [])
    this._setCheckboxes("gross_rent_filter[]", params.gross_rent_filter || [])
    this._setCheckboxes("price_movement[]", params.price_movement || [])
    this._setCheckboxes("true_rank_filter[]", params.true_rank_filter || [])

    // Trigger visibility updates (these use non-bubbling events
    // so they only reach the inline JS handlers, not the form listener)
    this._triggerVisibility("mf_pop_filter")
    this._triggerVisibility("mf_price_filter")
    this._triggerVisibility("mf_rank_select")

    // Delay clearing _populating so all cascading change events
    // (from Stimulus multiselect updates, visibility toggles, etc.)
    // are fully processed before we allow _onFormChange to deselect.
    setTimeout(() => { this._populating = false }, 0)
  }

  _setField(id, value) {
    const el = document.getElementById(id)
    if (el && value !== undefined) {
      el.value = String(value)
    }
  }

  _setCheckboxes(name, values) {
    if (!this.form) return
    const checkboxes = this.form.querySelectorAll(`input[name="${name}"]`)
    checkboxes.forEach(cb => {
      cb.checked = values.includes(cb.value)
    })
    // Trigger multiselect label update via Stimulus action
    if (checkboxes.length > 0) {
      checkboxes[0].dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  _triggerVisibility(id) {
    const el = document.getElementById(id)
    if (el) el.dispatchEvent(new Event("change"))
  }
}
